import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { api, type Item } from '../api.ts';
import { displayTitle, formatDuration } from '../format.ts';
import { ArrowRightIcon, CloseIcon, FilmIcon, SearchIcon, TvIcon } from '../Icon.tsx';
import { resizeProxyImage } from '../imageSize.ts';
import { mediaUrl } from '../mediaToken.ts';
import { useRoute } from '../route.ts';

const KIND_LABEL: Record<string, string> = {
  movie: 'Film',
  show: 'Dizi',
  season: 'Sezon',
  episode: 'Bölüm',
};

export function SearchBox({ onOpenItem }: { onOpenItem?: (itemId: string) => void }): ReactNode {
  const [route, navigate] = useRoute();
  const [text, setText] = useState(route.name === 'search' ? route.query : '');
  const [hits, setHits] = useState<Item[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  /** Klavyeyle gezilen sonuç; -1 = seçim yok. */
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /*
   * Kısayol ipucu platforma göre değişir. `⌘` Windows/Linux kullanıcısına
   * yanlış bilgi verir; işlev her iki tuş birleşimini de kabul eder.
   */
  const shortcutLabel =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent)
      ? '⌘K'
      : 'Ctrl K';

  // Sayfa rotası değişirse metni güncelle
  useEffect(() => {
    setText(route.name === 'search' ? route.query : '');
    setIsOpen(false);
    setActiveIndex(-1);
  }, [route]);

  // Canlı anlık arama (debounced)
  useEffect(() => {
    const trimmed = text.trim();
    if (trimmed.length < 2) {
      setHits([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setActiveIndex(-1);
    const timer = setTimeout(() => {
      api
        .search(trimmed)
        .then((results) => {
          setHits(results.hits.slice(0, 6));
          setLoading(false);
          // Dropdown açma kararı yalnız kullanıcı etkileşiminde verilir;
          // rota geçişi sonuç ekranının araç çubuğunu kapatmaz.
        })
        .catch(() => {
          setHits([]);
          setLoading(false);
        });
    }, 220);

    return () => clearTimeout(timer);
  }, [text]);

  // Tıklama dışarıya yapılırsa dropdown'ı kapat
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /*
   * D-15 · Global arama kısayolu.
   *
   * Arama ürünün en güçlü keşif aracı ama yalnız fareyle erişilebiliyordu.
   * `⌘K` / `Ctrl K` kutuyu odaklar ve mevcut metni seçer; `preventDefault`
   * tarayıcının kendi kısayolunu engeller. Global dinleyici yalnız bir kez
   * kurulur (bağımlılık yok).
   */
  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);

  const handleSelect = (itemId: string) => {
    setIsOpen(false);
    if (onOpenItem) {
      onOpenItem(itemId);
    } else {
      navigate({ name: 'item', itemId });
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const query = text.trim();
    if (query.length > 0) {
      setIsOpen(false);
      navigate({ name: 'search', query });
    }
  };

  return (
    <div className="search-container" ref={containerRef}>
      <search className="search">
        <form onSubmit={handleSubmit}>
          <div className="search__input-wrapper">
            <span className="search__icon" aria-hidden="true">
              <SearchIcon size={16} />
            </span>
            <input
              ref={inputRef}
              type="search"
              value={text}
              placeholder="Film, dizi veya kütüphanede ara…"
              aria-label="Ara"
              aria-keyshortcuts="Meta+K Control+K"
              role="combobox"
              aria-expanded={isOpen}
              aria-controls="search-results"
              aria-autocomplete="list"
              aria-activedescendant={
                activeIndex >= 0 && hits[activeIndex]
                  ? `search-hit-${hits[activeIndex].id}`
                  : undefined
              }
              onFocus={() => {
                if (text.trim().length >= 2 && hits.length > 0) setIsOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setIsOpen(false);
                  return;
                }
                if (!isOpen || hits.length === 0) return;
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  setActiveIndex((index) => (index + 1) % hits.length);
                  return;
                }
                if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  setActiveIndex((index) => (index <= 0 ? hits.length - 1 : index - 1));
                  return;
                }
                if (event.key === 'Enter' && activeIndex >= 0) {
                  const hit = hits[activeIndex];
                  if (hit) {
                    event.preventDefault();
                    handleSelect(hit.id);
                  }
                }
              }}
              onChange={(event) => {
                setText(event.target.value);
                setIsOpen(event.target.value.trim().length >= 2);
              }}
            />
            {text.length === 0 && (
              <kbd className="search__shortcut" title="Arama kısayolu">
                {shortcutLabel}
              </kbd>
            )}
            {text.length > 0 && (
              <button
                type="button"
                className="search__clear-btn"
                onClick={() => {
                  setText('');
                  setHits([]);
                  setIsOpen(false);
                }}
                aria-label="Aramayı temizle"
              >
                <CloseIcon size={14} />
              </button>
            )}
          </div>
        </form>
      </search>

      {isOpen && text.trim().length >= 2 && (
        <div className="search-dropdown" id="search-results">
          {/*
            B-35 · Sonuç sayısı ekran okuyucuya duyurulur.

            Açılır liste görsel olarak açılıyor ama ekran okuyucu kullanıcısı
            "kaç sonuç var" bilgisini alamıyordu. `aria-live` bölge metin
            değiştiğinde duyurulur.
          */}
          <span className="visually-hidden" role="status" aria-live="polite">
            {loading ? 'Aranıyor…' : `${hits.length} sonuç bulundu`}
          </span>

          {loading && <div className="search-dropdown__loading">Aranıyor…</div>}

          {!loading && hits.length === 0 && (
            <div className="search-dropdown__empty">&quot;{text}&quot; için sonuç bulunamadı.</div>
          )}

          {!loading && hits.length > 0 && (
            <>
              <div className="search-dropdown__list" role="listbox" aria-label="Arama sonuçları">
                {hits.map((item, index) => (
                  <button
                    type="button"
                    key={item.id}
                    id={`search-hit-${item.id}`}
                    role="option"
                    aria-selected={activeIndex === index}
                    className={`search-dropdown__item ${activeIndex === index ? 'is-active' : ''}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => handleSelect(item.id)}
                  >
                    <div className={`search-dropdown__thumb search-dropdown__thumb--${item.kind}`}>
                      <img
                        src={
                          resizeProxyImage(item.posterUrl, 'w185') ??
                          item.thumbnailUrl ??
                          mediaUrl(`/api/items/${item.id}/thumbnail`)
                        }
                        alt=""
                        loading="lazy"
                        decoding="async"
                        onError={(event) => {
                          const image = event.currentTarget;
                          const local = mediaUrl(`/api/items/${item.id}/thumbnail`);
                          if (item.posterUrl && !image.src.endsWith(local)) {
                            image.src = local;
                            return;
                          }
                          image.style.display = 'none';
                        }}
                      />
                      <span className="search-dropdown__fallback-icon">
                        {item.kind === 'movie' ? <FilmIcon size={20} /> : <TvIcon size={20} />}
                      </span>
                    </div>

                    <div className="search-dropdown__info">
                      <span className="search-dropdown__title">{displayTitle(item)}</span>
                      <div className="search-dropdown__meta">
                        <span className="badge">{KIND_LABEL[item.kind] ?? item.kind}</span>
                        {item.year && <span>{item.year}</span>}
                        {item.durationMs && <span>{formatDuration(item.durationMs)}</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <button
                type="button"
                className="search-dropdown__footer"
                onClick={() => {
                  setIsOpen(false);
                  navigate({ name: 'search', query: text.trim() });
                }}
              >
                Tüm sonuçları göster (Enter)
                <ArrowRightIcon size={14} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
